import type { ProtectedItemResponse, SurgeProtectionResponse } from "../services/surgeProtection";
import { EMPTY_QUANTITY, formatQuantity } from "../utils/formatQuantity";
import { StatusIcon } from "./StatusIcon";
import { bannerTone } from "./statusTone";
import { PROSPECTIVE_CURRENT_BASIS_LABELS, PROTECTION_LEVEL_BASIS_LABELS } from "./surgeProtectionLabels";

// The words of a design check with this module's own NO_SOLUTION words: a computed failure of
// the protection level, Uc, UT, a discharge parameter or the short-circuit rating (CTO rule 6).
const STATUS_LABELS: Record<SurgeProtectionResponse["status"], string> = {
  DESIGN_CHECK_PASSED: "Design check passed",
  REVIEW_REQUIRED: "Engineering review required",
  NO_SOLUTION: "SPD check not met",
};

const STATUS_TONE: Record<SurgeProtectionResponse["status"], "pass" | "fail" | "warning"> = {
  DESIGN_CHECK_PASSED: "pass",
  REVIEW_REQUIRED: "warning",
  NO_SOLUTION: "fail",
};

export type ItemKind = "checked" | "review";

/** How one protected item fared: nothing owed is checked; a figure, a source or a distance owed is a review. */
export function itemKind(item: ProtectedItemResponse): ItemKind {
  return item.status === "DESIGN_CHECK_PASSED" ? "checked" : "review";
}

/** The failed conditions named: "SPD check not met: protection level, Uc" or the other statuses' words. */
export function statusHeading(result: SurgeProtectionResponse): string {
  if (result.status !== "NO_SOLUTION") {
    return STATUS_LABELS[result.status];
  }
  const failed: string[] = [];
  if (result.protection_level_met === false) {
    failed.push("protection level");
  }
  if (result.uc_met === false) {
    failed.push("Uc");
  }
  if (result.ut_met === false) {
    failed.push("UT");
  }
  if (result.iimp_met === false) {
    failed.push("Iimp");
  }
  if (result.in_met === false) {
    failed.push("In");
  }
  if (result.imax_met === false) {
    failed.push("Imax");
  }
  if (result.isccr_met === false) {
    failed.push("short-circuit rating");
  }
  return `${STATUS_LABELS.NO_SOLUTION}: ${failed.join(", ")}`;
}

/** "1.98 kV of 1.8 kV" - Up_eff against the lowest Uw of the items; an em dash when not formed. */
export function protectionLevelText(result: SurgeProtectionResponse): string {
  if (result.up_eff_kv === null) {
    return EMPTY_QUANTITY;
  }
  return `${formatQuantity(result.up_eff_kv, "kV").display} of ${formatQuantity(result.lowest_uw_kv, "kV").display}`;
}

// The two ratios of the strip read in opposite directions (register follow-up 41): Uc over the
// required minimum is capacity / requirement and passes at or above 1; Ik'' over the rating is
// demand / capacity and passes at or below 1. Each ratio names its direction where it is printed.
export const UC_RATIO_WORDS = "Uc / required minimum";
export const UC_RATIO_PASS = "≥ 1 passes";
export const ISCCR_RATIO_WORDS = "demand / capacity";
export const ISCCR_RATIO_PASS = "≤ 1 passes";

/** "275 V of 253 V required minimum · Uc / required minimum 1.087 (≥ 1 passes)"; Uc alone when none. */
export function continuousVoltageText(result: SurgeProtectionResponse): string {
  const uc = formatQuantity(result.spd.uc_v.value, "V").display;
  const minimum = result.requirements.uc_minimum_v;
  if (minimum === null || result.uc_ratio === null) {
    return uc;
  }
  return `${uc} of ${formatQuantity(minimum.value, "V").display} required minimum · ${UC_RATIO_WORDS} ${formatQuantity(result.uc_ratio).display} (${UC_RATIO_PASS})`;
}

/** "36.2 kA of 50 kA rating · demand / capacity 0.724 (≤ 1 passes)"; dashes when not formed. */
export function shortCircuitText(result: SurgeProtectionResponse): string {
  const current =
    result.prospective_current_ka === null ? EMPTY_QUANTITY : formatQuantity(result.prospective_current_ka, "kA").display;
  const rating = result.spd.isccr_ka === null ? EMPTY_QUANTITY : formatQuantity(result.spd.isccr_ka.value, "kA").display;
  const ratio =
    result.isccr_ratio === null
      ? ""
      : ` · ${ISCCR_RATIO_WORDS} ${formatQuantity(result.isccr_ratio).display} (${ISCCR_RATIO_PASS})`;
  return `${current} of ${rating} rating${ratio}`;
}

type SurgeProtectionResultSummaryProps = {
  result: SurgeProtectionResponse;
};

// The decisive values first (Master Prompt v2.1 section 19): the outcome, Up_eff against the
// lowest Uw, its margin and basis, Uc against its minimum, the short-circuit rating against the
// prospective current and its basis. No figure is calculated here. Display rule A12: four
// significant figures on screen, the exact engine decimal in the tooltip (title attribute).
export function SurgeProtectionResultSummary({ result }: SurgeProtectionResultSummaryProps) {
  const tone = STATUS_TONE[result.status];
  const items = result.items;
  const review = items.filter((item) => itemKind(item) === "review").length;
  const notMet = items.filter((item) => item.met === false).length;
  const warningCount =
    result.warnings.length + items.reduce((count, item) => count + item.warnings.length, 0);

  const levelMargin = formatQuantity(result.protection_level_margin_kv, "kV");
  const dischargeWords = [
    result.spd.iimp_ka === null ? null : `Iimp ${formatQuantity(result.spd.iimp_ka.value, "kA").display}`,
    result.spd.in_ka === null ? null : `In ${formatQuantity(result.spd.in_ka.value, "kA").display}`,
    result.spd.imax_ka === null ? null : `Imax ${formatQuantity(result.spd.imax_ka.value, "kA").display}`,
  ].filter((word): word is string => word !== null);
  const dischargeExact = [
    result.spd.iimp_ka === null ? null : `Iimp ${result.spd.iimp_ka.value} kA`,
    result.spd.in_ka === null ? null : `In ${result.spd.in_ka.value} kA`,
    result.spd.imax_ka === null ? null : `Imax ${result.spd.imax_ka.value} kA`,
  ]
    .filter((word): word is string => word !== null)
    .join(" · ");
  const dischargeMet =
    result.iimp_met === false || result.in_met === false || result.imax_met === false
      ? "not met"
      : result.iimp_met === null && result.in_met === null && result.imax_met === null
        ? "not compared"
        : "met";

  return (
    <section aria-label="Result summary" data-summary-tone={tone}>
      <header>
        <p data-summary-status={result.status}>
          <StatusIcon tone={bannerTone(result.status, STATUS_TONE)} size={20} />
          {statusHeading(result)}
        </p>
        <p>
          Study <strong>{result.code}</strong> · {result.name} · {result.jurisdiction_profile} profile
        </p>
      </header>
      <dl data-summary-strip>
        <div>
          <dt>Protected items</dt>
          {/* Counts of items, not engineering quantities. */}
          <dd data-summary-field="items" data-summary-value="text">
            {items.length} {items.length === 1 ? "item" : "items"} · {notMet} not met · {review} review
          </dd>
        </div>
        <div>
          <dt>Up_eff · lowest Uw</dt>
          <dd
            data-summary-field="protection-level"
            title={result.up_eff_kv === null ? undefined : `${result.up_eff_kv} kV · ${result.lowest_uw_kv} kV`}
          >
            {protectionLevelText(result)}
          </dd>
        </div>
        <div>
          <dt>Protection-level margin</dt>
          <dd data-summary-field="protection-margin" title={levelMargin.exact ?? undefined}>
            {result.protection_level_margin_kv === null ? EMPTY_QUANTITY : levelMargin.display}
          </dd>
        </div>
        <div>
          <dt>Lead basis</dt>
          <dd
            data-summary-field="protection-basis"
            data-summary-value="text"
            data-protection-level-basis={result.protection_level_basis}
          >
            {PROTECTION_LEVEL_BASIS_LABELS[result.protection_level_basis]}
          </dd>
        </div>
        <div>
          <dt>Uc · required minimum</dt>
          <dd
            data-summary-field="uc"
            title={`${result.spd.uc_v.value} V${result.requirements.uc_minimum_v === null ? "" : ` · ${result.requirements.uc_minimum_v.value} V`}`}
          >
            {continuousVoltageText(result)}
          </dd>
        </div>
        <div>
          <dt>Discharge</dt>
          <dd data-summary-field="discharge" data-discharge-result={dischargeMet} title={dischargeExact || undefined}>
            {dischargeWords.length === 0 ? EMPTY_QUANTITY : dischargeWords.join(" · ")} · {dischargeMet}
          </dd>
        </div>
        <div>
          <dt>Ik'' · short-circuit rating</dt>
          <dd
            data-summary-field="short-circuit"
            title={
              result.prospective_current_ka === null
                ? undefined
                : `${result.prospective_current_ka} kA · ${result.spd.isccr_ka?.value ?? "—"} kA`
            }
          >
            {shortCircuitText(result)}
          </dd>
        </div>
        <div>
          <dt>Current basis</dt>
          <dd
            data-summary-field="short-circuit-basis"
            data-summary-value="text"
            data-prospective-current-basis={result.prospective_current_basis}
          >
            {PROSPECTIVE_CURRENT_BASIS_LABELS[result.prospective_current_basis]}
          </dd>
        </div>
        <div>
          <dt>Warnings</dt>
          <dd data-summary-warnings={warningCount}>{warningCount}</dd>
        </div>
      </dl>
    </section>
  );
}

export { STATUS_LABELS, STATUS_TONE };
export default SurgeProtectionResultSummary;
